// Single source of truth for ticket status → human label + badge variant.
// Reused on both the user and admin support surfaces so the colour coding
// never drifts between them.

import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { TicketStatus } from '@/lib/support';

interface StatusMeta {
  label: string;
  variant: 'default' | 'secondary' | 'outline';
  // Amber / blue aren't in the base badge variant set, so we tint via class
  // for the two "waiting" states. Keeps everything template-flavoured.
  className?: string;
}

const STATUS_META: Record<TicketStatus, StatusMeta> = {
  open: { label: 'Open', variant: 'default' },
  waiting_on_us: {
    label: 'Waiting on us',
    variant: 'outline',
    className: 'border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400',
  },
  waiting_on_customer: {
    label: 'Waiting on you',
    variant: 'outline',
    className: 'border-blue-500/40 bg-blue-500/10 text-blue-600 dark:text-blue-400',
  },
  closed: { label: 'Closed', variant: 'secondary' },
};

// Admin-side label override: "Waiting on customer" reads clearer than
// "Waiting on you" when an operator is triaging other people's tickets.
export function ticketStatusLabel(status: TicketStatus, admin = false): string {
  if (admin && status === 'waiting_on_customer') return 'Waiting on customer';
  return STATUS_META[status].label;
}

export function TicketStatusBadge({
  status,
  admin = false,
}: {
  status: TicketStatus;
  admin?: boolean;
}) {
  const meta = STATUS_META[status];
  return (
    <Badge variant={meta.variant} className={cn(meta.className)}>
      {ticketStatusLabel(status, admin)}
    </Badge>
  );
}
